import React from 'react'
import {
  SocialConfigContext,
  socialConfigReducer,
  UserContext,
  SignupContext,
} from 'context'
import {
  SocialConfigActionTypes,
  ISocialPlatform,
  SocialConfigState,
  ISocialBase,
  SignupSteps,
} from 'interfaces'

interface Props {
  children: React.ReactNode
}

const createSocialConfig = (social: ISocialPlatform) => {
  return {
    platform: social,
    audience: 0,
    identifier: '',
    isConfigured: false,
  }
}

const SocialConfigActionTypesArray = Object.values(ISocialPlatform).map((s) =>
  createSocialConfig(s)
)

const SocialConfig_INITIAL_STATE: SocialConfigState = {
  socials: SocialConfigActionTypesArray,
  isConfiguring: false,
  alertEmptySocials: false,
}

export const SocialConfigProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = React.useReducer(
    socialConfigReducer,
    SocialConfig_INITIAL_STATE
  )
  const { user, setUser } = React.useContext(UserContext)
  const { step, finishRegister } = React.useContext(SignupContext)

  React.useEffect(() => {
    const socialsInUser = user?.socials || []

    const socials = state.socials.map((s) => {
      const socialInUser = socialsInUser.find(
        (social) => social.platform === s.platform
      )
      return socialInUser ? { ...socialInUser, isConfigured: true } : s
    })

    dispatch({ type: SocialConfigActionTypes.SET_SOCIALS, payload: socials })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const setSocial = (social: ISocialBase) => {
    dispatch({
      type: SocialConfigActionTypes.SET_SOCIAL,
      payload: { ...social, isConfigured: true },
    })
    dispatch({
      type: SocialConfigActionTypes.SET_IS_CONFIGURING,
      payload: false,
    })
  }

  const removeSocial = () => {
    dispatch({
      type: SocialConfigActionTypes.REMOVE_SOCIAL,
      payload: state.configuringSocial.platform,
    })
    dispatch({
      type: SocialConfigActionTypes.SET_IS_CONFIGURING,
      payload: false,
    })
  }

  const setConfiguringSocial = (social: ISocialPlatform) => {
    const socialConfig = state.socials.find((s) => s.platform === social)
    dispatch({
      type: SocialConfigActionTypes.SET_CONFIGURING_SOCIAL,
      payload: socialConfig,
    })
    dispatch({
      type: SocialConfigActionTypes.SET_IS_CONFIGURING,
      payload: true,
    })
  }

  const cancelConfiguringSocial = () => {
    dispatch({
      type: SocialConfigActionTypes.SET_IS_CONFIGURING,
      payload: false,
    })
  }

  const saveSocials = (forceSave = false) => {
    dispatch({ type: SocialConfigActionTypes.SET_LOADING, payload: true })
    const socials = state.socials
      .filter((s) => s.isConfigured)
      .map((s) => ({
        platform: s.platform,
        audience: s.audience,
        identifier: s.identifier,
      }))

    setUser({ ...user, socials })

    if (socials.length === 0 && !forceSave) {
      dispatch({
        type: SocialConfigActionTypes.SET_ALERT_EMPTY_SOCIALS,
        payload: true,
      })
      dispatch({ type: SocialConfigActionTypes.SET_LOADING, payload: false })
      return
    }

    closeAlertEmptySocials()
    if (step === SignupSteps.SOCIALS_FORM) {
      setTimeout(async () => {
        await finishRegister({ socials })
        dispatch({ type: SocialConfigActionTypes.SET_LOADING, payload: false })
      })
    }
  }

  const closeAlertEmptySocials = () => {
    dispatch({
      type: SocialConfigActionTypes.SET_ALERT_EMPTY_SOCIALS,
      payload: false,
    })
  }

  return (
    <SocialConfigContext.Provider
      value={{
        ...state,
        setSocial,
        removeSocial,
        setConfiguringSocial,
        cancelConfiguringSocial,
        saveSocials,
        continueConfiguringSocial: closeAlertEmptySocials,
      }}
    >
      {children}
    </SocialConfigContext.Provider>
  )
}
